// Maintenance Mode Service
// Handles maintenance toggle state and admin overrides

import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, GetCommand, PutCommand } from '@aws-sdk/lib-dynamodb';
import { fetchAuthSession } from 'aws-amplify/auth';

// Client caching for performance optimization
let cachedClient = null;
let cachedCredentials = null;
let credentialsExpiry = null;

// Function to get DynamoDB client with Cognito credentials (optimized with caching)
const getDynamoClient = async () => {
  try {
    const now = Date.now();
    
    // Check if we have valid cached credentials (refresh 5 minutes before expiry)
    if (cachedClient && cachedCredentials && credentialsExpiry && 
        now < (credentialsExpiry - 300000)) {
      return cachedClient;
    }

    const session = await fetchAuthSession();
    
    if (!session.credentials) {
      throw new Error('No valid credentials found. User may not be authenticated.');
    }

    // Cache credentials and set expiry
    cachedCredentials = session.credentials;
    credentialsExpiry = session.credentials.expiration ? 
      new Date(session.credentials.expiration).getTime() : 
      now + 3600000; // Default 1 hour if no expiration

    const client = new DynamoDBClient({
      region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
      credentials: cachedCredentials,
    });

    cachedClient = DynamoDBDocumentClient.from(client);
    return cachedClient;
  } catch (error) {
    // Clear cache on error
    cachedClient = null;
    cachedCredentials = null;
    credentialsExpiry = null;
    throw error;
  }
};

// Environment-based table selection
const SETTINGS_TABLE = process.env.NODE_ENV === 'production' 
  ? 'asl-ceu-tracker-settings' 
  : 'asl-ceu-tracker-settings-dev';

// In-memory cache for maintenance status (reduces DynamoDB calls)
let maintenanceCache = null;
let cacheExpiry = null;
const CACHE_DURATION = 60000; // 1 minute cache

export const maintenanceService = {
  // Get maintenance mode status (with caching)
  getMaintenanceStatus: async () => {
    try {
      const now = Date.now();
      
      // Return cached result if still valid
      if (maintenanceCache && cacheExpiry && now < cacheExpiry) {
        return maintenanceCache;
      }

      const docClient = await getDynamoClient();
      
      const command = new GetCommand({
        TableName: SETTINGS_TABLE,
        Key: { settingKey: 'maintenance_mode' },
        // Optimize for eventual consistency (faster, cheaper reads)
        ConsistentRead: false
      });

      const result = await docClient.send(command);
      
      const status = {
        isMaintenanceMode: result.Item?.isActive || false,
        isCommunityTestingEnded: result.Item?.isCommunityTestingEnded || false,
        message: result.Item?.message || 'CertifiAble is currently under maintenance. Thank you for your patience.',
        feedbackUrl: result.Item?.feedbackUrl || 'https://docs.google.com/forms/d/e/1FAIpQLSffs0yIUCLLGDDyHR6zCEviKGS2M1V8TooQlK6_X-3w-0PjCw/viewform?usp=preview',
        lastUpdated: result.Item?.lastUpdated,
        updatedBy: result.Item?.updatedBy
      };

      // Cache the result
      maintenanceCache = status;
      cacheExpiry = now + CACHE_DURATION;
      
      return status;
    } catch (error) {
      console.error('Error getting maintenance status:', error);
      // Default to not in maintenance mode if we can't check
      const fallbackStatus = {
        isMaintenanceMode: false,
        isCommunityTestingEnded: true, // Default to showing community testing ended page
        message: 'CertifiAble is currently under maintenance. Thank you for your patience.',
        feedbackUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSffs0yIUCLLGDDyHR6zCEviKGS2M1V8TooQlK6_X-3w-0PjCw/viewform?usp=preview'
      };
      
      // Cache fallback for shorter duration to retry sooner
      maintenanceCache = fallbackStatus;
      cacheExpiry = Date.now() + 10000; // 10 seconds for error cases
      
      return fallbackStatus;
    }
  },

  // Set maintenance mode status (admin only) - clears cache
  setMaintenanceStatus: async (isActive, message, feedbackUrl, userId, isCommunityTestingEnded = false) => {
    try {
      const docClient = await getDynamoClient();
      
      const command = new PutCommand({
        TableName: SETTINGS_TABLE,
        Item: {
          settingKey: 'maintenance_mode',
          isActive,
          isCommunityTestingEnded,
          message: message || 'CertifiAble is currently under maintenance. Thank you for your patience.',
          feedbackUrl: feedbackUrl || 'https://docs.google.com/forms/d/e/1FAIpQLSffs0yIUCLLGDDyHR6zCEviKGS2M1V8TooQlK6_X-3w-0PjCw/viewform?usp=preview',
          lastUpdated: new Date().toISOString(),
          updatedBy: userId
        }
      });

      await docClient.send(command);
      
      // Clear cache after update to ensure fresh data
      maintenanceCache = null;
      cacheExpiry = null;
      
      return { success: true };
    } catch (error) {
      console.error('Error setting maintenance status:', error);
      throw error;
    }
  },

  // Clear cache manually (useful for testing or forced refresh)
  clearCache: () => {
    maintenanceCache = null;
    cacheExpiry = null;
  }
};

// Local storage keys for admin overrides
const ADMIN_VIEW_MODE_KEY = 'certifiable_admin_view_mode';

export const adminViewService = {
  // Get current admin view mode
  getViewMode: () => {
    return localStorage.getItem(ADMIN_VIEW_MODE_KEY) || 'admin'; // 'admin', 'user', 'live'
  },

  // Set admin view mode
  setViewMode: (mode) => {
    localStorage.setItem(ADMIN_VIEW_MODE_KEY, mode);
  },

  // Check if admin should see maintenance mode
  shouldShowMaintenance: (isMaintenanceMode, isAdmin) => {
    if (!isMaintenanceMode) return false;
    if (!isAdmin) return true;
    
    const viewMode = adminViewService.getViewMode();
    return viewMode === 'user'; // Only show maintenance to admin if in 'user' view mode
  },

  // Check if admin should see live dashboard
  shouldShowLiveDashboard: (isMaintenanceMode, isAdmin) => {
    if (!isMaintenanceMode) return true;
    if (!isAdmin) return false;
    
    const viewMode = adminViewService.getViewMode();
    return viewMode === 'live'; // Show live dashboard if admin is in 'live' view mode
  }
};